import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, MapPin, Monitor, Plus, ChevronRight, ChevronDown, Circle, LayoutGrid, Pencil, Archive, Trash2, MoreVertical, Activity, Settings2, Landmark, Rocket, PowerOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KioskPanel } from "./KioskPanel";
import { LocationPanel } from "./LocationPanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Hierarchy() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "super_duper_admin";
  const [tree, setTree] = useState([]); // grouped-tree: [{group, buildings:[{...,org_name,locations}]}]
  const [orgs, setOrgs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);
  const [value, setValue] = useState({});
  const [displayModal, setDisplayModal] = useState(false);
  const [panelKiosk, setPanelKiosk] = useState(null);
  const [panelLocation, setPanelLocation] = useState(null);
  const [display, setDisplay] = useState({ grid_cols_landscape: 4, grid_cols_portrait: 2, screen_dim_minutes: 0 });

  const groupKey = (g) => (g ? `group:${g.name}` : "group:__ungrouped__");
  const load = () => Promise.all([
    api.get("/hierarchy/grouped-tree"),
    api.get("/organisations"),
    api.get("/building-groups").catch(() => ({ data: [] })),
  ]).then(([tr, or, gr]) => {
    setTree(tr.data);
    setOrgs(or.data);
    setGroups(gr.data);
    const exp = {};
    tr.data.forEach((sec) => {
      exp[groupKey(sec.group)] = true;
      sec.buildings.forEach((b) => { exp[b.id] = true; b.locations.forEach((l) => { exp[l.id] = true; }); });
    });
    setExpanded((e) => ({ ...exp, ...e }));
  });
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings/display").then((r) => setDisplay(r.data)).catch(() => {}); }, []);

  const saveDisplay = async () => {
    try {
      const r = await api.put("/settings/display", {
        grid_cols_landscape: Number(display.grid_cols_landscape) || 4,
        grid_cols_portrait: Number(display.grid_cols_portrait) || 2,
        screen_dim_minutes: Number(display.screen_dim_minutes) || 0,
      });
      setDisplay(r.data);
      toast.success("Display settings saved");
      setDisplayModal(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const [activating, setActivating] = useState(null);
  const [confirmKiosk, setConfirmKiosk] = useState(null);

  // Flips a kiosk between live/draft based on its current `activated` flag. Returns true on success.
  const performToggle = async (k) => {
    setActivating(k.id);
    try {
      await api.post(`/kiosks/${k.id}/${k.activated ? "deactivate" : "activate"}`);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update kiosk");
      return false;
    } finally {
      setActivating(null);
    }
  };

  const confirmToggle = async () => {
    const k = confirmKiosk;
    setConfirmKiosk(null);
    if (!k) return;
    const wasActivated = k.activated;
    const ok = await performToggle(k);
    if (!ok) return;
    toast.success(wasActivated ? `${k.name} deactivated — back to draft` : `${k.name} is now live`, {
      action: {
        label: "Undo",
        onClick: () => performToggle({ ...k, activated: !wasActivated }),
      },
    });
  };

  const promoteOrg = async (sec) => {
    const orgId = sec.buildings[0]?.org_id;
    if (!orgId) { toast.error("This group has no buildings to promote."); return; }
    try {
      const r = await api.post("/building-groups/promote-org", { org_id: orgId, name: sec.group.name });
      toast.success(`“${r.data.name}” is now a Building Group — add a logo & rebate/charity in Building Groups.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [delOrg, setDelOrg] = useState(null);
  const [delGroup, setDelGroup] = useState(null);
  const [typed, setTyped] = useState("");
  const [orgsModal, setOrgsModal] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  const archiveOrg = async (org, archived) => {
    try {
      await api.post(`/organisations/${org.id}/archive`, { archived });
      toast.success(archived ? "Organisation archived" : "Organisation restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const buildingCountForOrg = (orgId) => tree.reduce((n, sec) => n + sec.buildings.filter((b) => b.org_id === orgId).length, 0);
  const startDelete = (org) => { setTyped(""); setDelOrg({ org, needsCascade: buildingCountForOrg(org.id) > 0, count: buildingCountForOrg(org.id) }); };
  const confirmDelete = async () => {
    const { org, needsCascade } = delOrg;
    try {
      await api.delete(`/organisations/${org.id}${needsCascade ? "?cascade=true" : ""}`);
      toast.success("Organisation deleted");
      setDelOrg(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startDeleteGroup = (sec) => { setTyped(""); setDelGroup({ name: sec.group.name, count: sec.buildings.length }); };
  const confirmDeleteGroup = async () => {
    try {
      await api.delete(`/building-groups/${encodeURIComponent(delGroup.name)}`);
      toast.success(delGroup.count > 0 ? "Group deleted — its buildings moved to Ungrouped" : "Group deleted");
      setDelGroup(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const nextKioskCode = () => {
    let max = 0;
    tree.forEach((sec) => sec.buildings.forEach((b) => b.locations.forEach((l) => (l.kiosks || []).forEach((k) => {
      const c = k.code || "";
      if (c.startsWith("KIOSK-") && /^\d+$/.test(c.slice(6))) max = Math.max(max, parseInt(c.slice(6), 10));
    }))));
    return `KIOSK-${String(max + 1).padStart(3, "0")}`;
  };
  const openModal = (type, parent) => {
    const init = { inactivity_timeout: 60 };
    if (type === "kiosk") { init.code = nextKioskCode(); init.count = 1; }
    setModal({ type, parent });
    setValue(init);
  };
  const openNewBuilding = (presetGroup = "") => {
    setModal({ type: "building" });
    setValue({ name: "", address: "", group: presetGroup, org_id: orgs[0]?.id || "", rebate_mode: "inherit", charity_mode: "inherit", rebate_percent: "", charity_percent: "", charity_name: "", charity_show_idle: false, charity_show_receipt: false });
  };
  const openEditOrg = (org) => { setModal({ type: "org", editId: org.id }); setValue({ name: org.name, urn: org.urn || "", logo_url: org.logo_url || "" }); };
  const openNewOrg = () => { setModal({ type: "org" }); setValue({ name: "", urn: "", logo_url: "" }); };
  const openEditBuilding = (b) => {
    const mode = (en) => (en == null ? "inherit" : en ? "on" : "off");
    setModal({ type: "building", editId: b.id });
    setValue({ name: b.name, address: b.address || "", group: b.group || "", org_id: b.org_id,
      rebate_mode: mode(b.rebate_enabled), rebate_percent: b.rebate_percent ?? "",
      charity_mode: mode(b.charity_enabled), charity_percent: b.charity_percent ?? "",
      charity_name: b.charity_name ?? "", charity_show_idle: !!b.charity_show_idle, charity_show_receipt: !!b.charity_show_receipt });
  };

  const submit = async () => {
    try {
      if (modal.type === "org") {
        if (modal.editId) await api.put(`/organisations/${modal.editId}`, { name: value.name, urn: value.urn || "", logo_url: value.logo_url || "" });
        else await api.post("/organisations", { name: value.name, urn: value.urn || "", logo_url: value.logo_url || "" });
      }
      if (modal.type === "building") {
        if (!value.org_id) { toast.error("Please choose an organisation"); return; }
        const mf = (mode, percent) => mode === "inherit" ? { enabled: null, percent: null } : mode === "off" ? { enabled: false, percent: null } : { enabled: true, percent: Number(percent) || 0 };
        const rb = mf(value.rebate_mode || "inherit", value.rebate_percent);
        const ch = mf(value.charity_mode || "inherit", value.charity_percent);
        const nOrOff = (mode, on, off) => mode === "on" ? on : mode === "off" ? off : null;
        const cm = value.charity_mode || "inherit";
        const finance = {
          rebate_enabled: rb.enabled, rebate_percent: rb.percent,
          charity_enabled: ch.enabled, charity_percent: ch.percent,
          charity_name: nOrOff(cm, value.charity_name || "", ""),
          charity_show_idle: nOrOff(cm, !!value.charity_show_idle, false),
          charity_show_receipt: nOrOff(cm, !!value.charity_show_receipt, false),
        };
        const base = { org_id: value.org_id, name: value.name, address: value.address || "", group: value.group || "", ...finance };
        if (modal.editId) await api.put(`/buildings/${modal.editId}`, base);
        else await api.post("/buildings", base);
      }
      if (modal.type === "location") await api.post("/locations", { building_id: modal.parent, name: value.name });
      if (modal.type === "kiosk") {
        const count = Number(value.count) || 1;
        if (count > 1) await api.post("/kiosks/bulk", { location_id: modal.parent, name: value.name, count, inactivity_timeout: Number(value.inactivity_timeout) || 60 });
        else await api.post("/kiosks", { location_id: modal.parent, name: value.name, code: value.code, inactivity_timeout: Number(value.inactivity_timeout) || 60 });
      }
      toast.success(modal.editId ? "Saved" : "Created");
      setModal(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const statusColor = { online: "text-emerald-500", offline: "text-slate-400", maintenance: "text-amber-500" };

  const renderBuilding = (b) => (
    <div key={b.id} className="rounded-lg border border-slate-100 dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
        <button onClick={() => toggle(b.id)}>{expanded[b.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
        <Building2 className="h-4 w-4 text-slate-500" />
        <span className="font-medium text-slate-800 dark:text-slate-200">{b.name}</span>
        <span className="text-xs text-slate-400">{b.address}</span>
        {b.org_name && b.group ? <Badge variant="secondary" data-testid={`building-org-${b.id}`} className="text-xs">{b.org_name}</Badge> : null}
        {isAdmin && <button data-testid={`building-edit-${b.id}`} title="Edit building" onClick={() => openEditBuilding(b)} className="ml-auto text-slate-400 hover:text-blue-600 p-1"><Pencil className="h-3.5 w-3.5" /></button>}
        {isAdmin && <button data-testid={`hierarchy-add-location-${b.id}`} onClick={() => openModal("location", b.id)} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Plus className="h-3 w-3" /> Location</button>}
      </div>
      {expanded[b.id] && (
        <div className="pl-3 sm:pl-8 pr-3 pb-2 space-y-2">
          {b.locations.map((l) => (
            <div key={l.id} className="rounded-lg bg-slate-50 dark:bg-slate-900 p-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <MapPin className="h-4 w-4 text-slate-400" />
                <button type="button" data-testid={`location-node-${l.id}`} onClick={() => setPanelLocation(l.id)} className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 hover:underline">{l.name}</button>
                <button type="button" data-testid={`location-activity-${l.id}`} onClick={() => setPanelLocation(l.id)} title="View location activity" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Activity className="h-3 w-3" /> Activity</button>
                {isAdmin && <button data-testid={`hierarchy-add-kiosk-${l.id}`} onClick={() => openModal("kiosk", l.id)} className="ml-auto text-xs text-blue-600 hover:underline flex items-center gap-1"><Plus className="h-3 w-3" /> Kiosk</button>}
              </div>
              <div className="mt-2 grid gap-2 lg:grid-cols-2">
                {l.kiosks.map((k) => (
                  <div key={k.id} role="button" tabIndex={0} onClick={() => setPanelKiosk(k.id)} data-testid={`kiosk-node-${k.code}`}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 hover:border-blue-500 transition-colors text-left w-full min-w-0 cursor-pointer">
                    <Monitor className="h-4 w-4 text-slate-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{k.name}</p>
                      <p className="text-xs font-mono-x text-slate-400">{k.code}</p>
                    </div>
                    <span className="ml-auto flex items-center gap-2 text-xs shrink-0">
                      {!k.activated && <span data-testid={`kiosk-draft-${k.code}`} className="font-bold uppercase rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px]">Draft</span>}
                      <span className="flex items-center gap-1">
                        <Circle className={`h-2 w-2 fill-current ${statusColor[k.status] || "text-slate-400"}`} />
                        {k.status}
                      </span>
                    </span>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                    {isAdmin && (
                      <button
                        type="button"
                        data-testid={`kiosk-activate-${k.code}`}
                        onClick={(e) => { e.stopPropagation(); setConfirmKiosk(k); }}
                        disabled={activating === k.id}
                        title={k.activated ? "Deactivate kiosk" : "Activate kiosk"}
                        className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-1 rounded-md text-xs font-semibold px-2.5 py-2 sm:py-1 transition-colors disabled:opacity-40 ${
                          k.activated
                            ? "border border-rose-200 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white"
                        }`}
                      >
                        {k.activated ? <><PowerOff className="h-3 w-3" /> Deactivate</> : <><Rocket className="h-3 w-3" /> Activate</>}
                      </button>
                    )}
                    <button type="button" data-testid={`kiosk-open-${k.code}`} onClick={(e) => { e.stopPropagation(); setPanelKiosk(k.id); }} title="Open kiosk management"
                      className="flex-1 sm:flex-none justify-center inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-2.5 py-2 sm:py-1 transition-colors">
                      <Settings2 className="h-3 w-3" /> Open kiosk
                    </button>
                    </div>
                  </div>
                ))}
                {!l.kiosks.length && <p className="text-xs text-slate-400 px-1">No kiosks yet</p>}
              </div>
            </div>
          ))}
          {!b.locations.length && <p className="text-xs text-slate-400">No locations yet</p>}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Sites & Kiosks</h1>
          <p className="text-slate-500 mt-1">Group → Building → Location → Kiosk</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button data-testid="hierarchy-add-org" variant="outline" onClick={openNewOrg}><Plus className="h-4 w-4 mr-1.5" /> Organisation</Button>
            <Button data-testid="hierarchy-add-building" onClick={() => openNewBuilding("")} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Building</Button>
          </div>
        )}
      </div>

      {isAdmin && orgs.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-slate-900 flex items-center justify-center"><Building2 className="h-4 w-4 text-blue-600" /></div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Organisations</p>
            <p className="text-xs text-slate-500">{orgs.length} organisation{orgs.length === 1 ? "" : "s"}{orgs.filter((o) => o.archived).length ? ` · ${orgs.filter((o) => o.archived).length} archived` : ""}</p>
          </div>
          <Button data-testid="manage-orgs-open" variant="outline" className="ml-auto" onClick={() => { setOrgSearch(""); setOrgsModal(true); }}><Settings2 className="h-4 w-4 mr-1.5" /> Manage organisations</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-slate-900 flex items-center justify-center"><LayoutGrid className="h-4 w-4 text-blue-600" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">Kiosk display layout</p>
          <p className="text-xs text-slate-500">Applies to all kiosks · {display.grid_cols_landscape} per row (landscape) · {display.grid_cols_portrait} per row (portrait)</p>
        </div>
        <Button data-testid="display-settings-open" variant="outline" className="ml-auto" onClick={() => setDisplayModal(true)}>Configure</Button>
      </div>

      <div className="space-y-3">
        {tree.map((sec) => {
          const gk = groupKey(sec.group);
          const isUngrouped = !sec.group;
          return (
            <div key={gk} data-testid={isUngrouped ? "group-section-ungrouped" : `group-section-${sec.group.name}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 sm:px-4 py-3 bg-gradient-to-r from-indigo-50 to-white dark:from-slate-900 dark:to-slate-950">
                <button onClick={() => toggle(gk)}>{expanded[gk] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
                <div className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden">
                  {sec.group?.logo_url ? <img src={resolveUrl(sec.group.logo_url)} alt={sec.group.name} className="h-full w-full object-contain" data-testid={`group-section-logo-${sec.group.name}`} /> : <Landmark className="h-4 w-4 text-indigo-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-slate-900 dark:text-white">{isUngrouped ? "Ungrouped" : sec.group.name}</span>
                  {sec.group?.from_org && <span data-testid={`group-fromorg-${sec.group.name}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 rounded">from organisation</span>}
                  <span className="ml-2 text-xs text-slate-400">{sec.buildings.length} building{sec.buildings.length === 1 ? "" : "s"}</span>
                </div>
                {isAdmin && !isUngrouped && (
                  <div className="w-full sm:w-auto ml-auto flex flex-wrap items-center justify-end gap-3">
                    {sec.group?.from_org && <button data-testid={`group-promote-${sec.group.name}`} onClick={() => promoteOrg(sec)} title="Turn this organisation into a first-class Building Group (logo + rebate/charity)" className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1"><Landmark className="h-3 w-3" /> Make a group</button>}
                    <button data-testid={`group-add-building-${sec.group.name}`} onClick={() => openNewBuilding(sec.group.name)} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Plus className="h-3 w-3" /> Building</button>
                    {!sec.group?.from_org && <button data-testid={`group-delete-${sec.group.name}`} onClick={() => startDeleteGroup(sec)} title="Delete this group" className="text-xs text-rose-600 hover:underline flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>}
                  </div>
                )}
              </div>
              {expanded[gk] && (
                <div className="p-2 sm:p-3 space-y-2">
                  {sec.buildings.map(renderBuilding)}
                  {!sec.buildings.length && <p className="text-xs text-slate-400 px-2">No buildings in this group yet</p>}
                </div>
              )}
            </div>
          );
        })}
        {!tree.length && <p className="text-slate-400 text-sm px-2">No groups or buildings yet. Create a group in Building Groups, then add a building.</p>}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{modal?.editId ? (modal.type === "building" ? "Edit building" : "Edit organisation") : `New ${modal?.type}`}</DialogTitle><DialogDescription>{modal?.type === "building" ? "Assign the building to an organisation and a building group." : modal?.type === "org" ? "Organisations own buildings and appear on the kiosk header." : "Fill in the details below."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {["building", "location", "kiosk"].includes(modal?.type) && (
              <p data-testid="hierarchy-public-note" className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                <span aria-hidden>⚠️</span>
                <span>This name is shown to customers on the kiosk. Please double-check spelling and capitalisation before saving.</span>
              </p>
            )}
            <div><Label>Name</Label><Input data-testid="hierarchy-form-name" value={value.name || ""} onChange={(e) => setValue({ ...value, name: e.target.value })} className="mt-1" /></div>
            {modal?.type === "org" && <div><Label>URN (Unique Reference Number)</Label><Input data-testid="hierarchy-form-urn" value={value.urn || ""} onChange={(e) => setValue({ ...value, urn: e.target.value })} placeholder="e.g. 10032541" className="mt-1" /></div>}
            {modal?.type === "org" && <div><Label>Customer logo URL (optional)</Label><Input data-testid="hierarchy-form-logo" value={value.logo_url || ""} onChange={(e) => setValue({ ...value, logo_url: e.target.value })} placeholder="https://…/logo.png" className="mt-1" /><p className="text-xs text-slate-400 mt-1">Shown on the kiosk header. Any shape — it's fit without cropping.</p></div>}
            {modal?.type === "building" && (
              <div><Label>Organisation</Label>
                <select data-testid="building-org-select" value={value.org_id || ""} onChange={(e) => setValue({ ...value, org_id: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 text-sm">
                  <option value="">Select an organisation…</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}
            {modal?.type === "building" && (
              <div><Label>Building group</Label>
                <select data-testid="building-group-select" value={value.group || ""} onChange={(e) => setValue({ ...value, group: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 text-sm">
                  <option value="">Ungrouped</option>
                  {groups.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">Groups are managed in Building Groups (logo + rebate/charity). Buildings inherit the group's rebate/charity unless overridden below.</p>
              </div>
            )}
            {modal?.type === "building" && <div><Label>Address</Label><Input value={value.address || ""} onChange={(e) => setValue({ ...value, address: e.target.value })} className="mt-1" /></div>}
            {modal?.type === "building" && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-3" data-testid="building-finance">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Rebate &amp; Charity <span className="font-normal text-slate-400">— “Inherit” uses the building group’s setting</span></p>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div><Label className="text-xs">Rebate (% of net sales)</Label>
                    <select data-testid="building-rebate-mode" value={value.rebate_mode || "inherit"} onChange={(e) => setValue({ ...value, rebate_mode: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 text-sm"><option value="inherit">Inherit group</option><option value="off">Off</option><option value="on">On</option></select>
                  </div>
                  {value.rebate_mode === "on" && <div><Label className="text-xs">Rebate %</Label><Input data-testid="building-rebate-percent" type="number" step="0.1" value={value.rebate_percent ?? ""} onChange={(e) => setValue({ ...value, rebate_percent: e.target.value })} className="mt-1 h-9" /></div>}
                </div>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div><Label className="text-xs">Charity (% of net sales)</Label>
                    <select data-testid="building-charity-mode" value={value.charity_mode || "inherit"} onChange={(e) => setValue({ ...value, charity_mode: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 text-sm"><option value="inherit">Inherit group</option><option value="off">Off</option><option value="on">On</option></select>
                  </div>
                  {value.charity_mode === "on" && <div><Label className="text-xs">Charity %</Label><Input data-testid="building-charity-percent" type="number" step="0.1" value={value.charity_percent ?? ""} onChange={(e) => setValue({ ...value, charity_percent: e.target.value })} className="mt-1 h-9" /></div>}
                </div>
                {value.charity_mode === "on" && (
                  <>
                    <div><Label className="text-xs">Charity name</Label><Input data-testid="building-charity-name" value={value.charity_name || ""} onChange={(e) => setValue({ ...value, charity_name: e.target.value })} placeholder="e.g. Shelter" className="mt-1 h-9" /></div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" data-testid="building-charity-idle" checked={!!value.charity_show_idle} onChange={(e) => setValue({ ...value, charity_show_idle: e.target.checked })} /> Show on idle screen</label>
                      <label className="flex items-center gap-2"><input type="checkbox" data-testid="building-charity-receipt" checked={!!value.charity_show_receipt} onChange={(e) => setValue({ ...value, charity_show_receipt: e.target.checked })} /> Show on receipt</label>
                    </div>
                  </>
                )}
              </div>
            )}
            {modal?.type === "kiosk" && (
              <>
                <div><Label>How many kiosks?</Label><Input type="number" min="1" max="20" data-testid="hierarchy-form-count" value={value.count || 1} onChange={(e) => setValue({ ...value, count: e.target.value })} className="mt-1" /><p className="text-xs text-slate-400 mt-1">Create several at once — extra kiosks get numbered names and auto-incremented codes.</p></div>
                {Number(value.count || 1) <= 1 && <div><Label>Kiosk code (unique)</Label><Input data-testid="hierarchy-form-code" value={value.code || ""} onChange={(e) => setValue({ ...value, code: e.target.value })} placeholder="KIOSK-005" className="mt-1" /><p className="text-xs text-slate-400 mt-1">Auto-suggested — leave as-is or change it. Must be unique.</p></div>}
                <div><Label>Inactivity timeout (seconds)</Label><Input type="number" value={value.inactivity_timeout || 60} onChange={(e) => setValue({ ...value, inactivity_timeout: e.target.value })} className="mt-1" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button data-testid="hierarchy-form-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700">{modal?.editId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmKiosk} onOpenChange={(o) => !o && setConfirmKiosk(null)}>
        <AlertDialogContent data-testid="kiosk-activate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmKiosk?.activated ? `Deactivate ${confirmKiosk?.name}?` : `Activate ${confirmKiosk?.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKiosk?.activated
                ? "This takes the kiosk offline and returns it to Draft. Customers won’t be able to use it until you activate it again."
                : "This makes the kiosk live so customers can start using it straight away."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kiosk-activate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="kiosk-activate-confirm"
              onClick={confirmToggle}
              className={confirmKiosk?.activated ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {confirmKiosk?.activated ? "Deactivate" : "Activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delOrg} onOpenChange={(o) => !o && setDelOrg(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delOrg?.org?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {delOrg?.needsCascade
                ? `This organisation has ${delOrg?.count} building(s). Deleting permanently removes all its buildings, floors, kiosks and their stock. Type the organisation name below to confirm.`
                : "This permanently deletes the organisation. This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {delOrg?.needsCascade && (
            <Input data-testid="org-delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={delOrg?.org?.name} className="mt-1" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="org-delete-confirm" disabled={delOrg?.needsCascade && typed !== delOrg?.org?.name} onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              {delOrg?.needsCascade ? "Delete everything" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delGroup} onOpenChange={(o) => !o && setDelGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{delGroup?.name}” group?</AlertDialogTitle>
            <AlertDialogDescription>
              {delGroup?.count > 0
                ? `This group has ${delGroup?.count} building(s). Deleting the group keeps the buildings (they move to Ungrouped) and removes the group's logo & rebate/charity settings. Type the group name below to confirm.`
                : `This permanently removes the empty “${delGroup?.name}” group. Type the group name below to confirm.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input data-testid="group-delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={delGroup?.name} className="mt-1" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="group-delete-confirm" disabled={typed !== delGroup?.name} onClick={confirmDeleteGroup} className="bg-rose-600 hover:bg-rose-700">
              Delete group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={orgsModal} onOpenChange={setOrgsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Organisations</DialogTitle>
            <DialogDescription>Organisations own buildings and appear on the kiosk header. Search, edit, archive or remove them here.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input data-testid="manage-orgs-search" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="Search organisations…" className="flex-1" />
            <Button data-testid="manage-orgs-new" onClick={() => { setOrgsModal(false); openNewOrg(); }} className="bg-blue-600 hover:bg-blue-700 shrink-0"><Plus className="h-4 w-4 mr-1.5" /> New</Button>
          </div>
          <div className="mt-1 max-h-[50vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
            {orgs.filter((o) => (o.name || "").toLowerCase().includes(orgSearch.toLowerCase()) || (o.urn || "").toLowerCase().includes(orgSearch.toLowerCase())).map((org) => {
              const count = buildingCountForOrg(org.id);
              return (
                <div key={org.id} data-testid={`manage-org-row-${org.id}`} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden">
                    {org.logo_url ? <img src={resolveUrl(org.logo_url)} alt={org.name} className="h-full w-full object-contain" /> : <Building2 className="h-4 w-4 text-blue-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate flex items-center gap-2">{org.name}{org.archived ? <Badge variant="secondary" className="text-[10px]" data-testid={`org-archived-${org.id}`}>Archived</Badge> : null}</p>
                    <p className="text-xs text-slate-400 truncate">{count} building{count === 1 ? "" : "s"}{org.urn ? ` · URN ${org.urn}` : ""}</p>
                  </div>
                  <button data-testid={`hierarchy-edit-org-${org.id}`} title="Edit" onClick={() => { setOrgsModal(false); openEditOrg(org); }} className="text-slate-400 hover:text-blue-600 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`org-actions-${org.id}`} title="Actions" className="text-slate-400 hover:text-slate-700 p-1.5"><MoreVertical className="h-4 w-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem data-testid={`org-archive-${org.id}`} onClick={() => archiveOrg(org, !org.archived)}><Archive className="h-4 w-4 mr-2" /> {org.archived ? "Restore" : "Archive"}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`org-delete-${org.id}`} onClick={() => { setOrgsModal(false); startDelete(org); }} className="text-rose-600 focus:text-rose-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
            {!orgs.filter((o) => (o.name || "").toLowerCase().includes(orgSearch.toLowerCase()) || (o.urn || "").toLowerCase().includes(orgSearch.toLowerCase())).length && (
              <p className="px-3 py-6 text-center text-sm text-slate-400" data-testid="manage-orgs-empty">No organisations match “{orgSearch}”.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={displayModal} onOpenChange={setDisplayModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kiosk display layout</DialogTitle><DialogDescription>Set how many products show per row. Applies to every kiosk.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Products per row — Landscape (2–6)</Label>
              <Input data-testid="display-cols-landscape" type="number" min={2} max={6} value={display.grid_cols_landscape} onChange={(e) => setDisplay({ ...display, grid_cols_landscape: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Products per row — Portrait (1–3)</Label>
              <Input data-testid="display-cols-portrait" type="number" min={1} max={3} value={display.grid_cols_portrait} onChange={(e) => setDisplay({ ...display, grid_cols_portrait: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Sleep screen after (minutes idle)</Label>
              <Input data-testid="display-screen-dim" type="number" min={0} max={60} value={display.screen_dim_minutes ?? 0} onChange={(e) => setDisplay({ ...display, screen_dim_minutes: e.target.value })} className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">Blacks out the kiosk and lets the tablet power its screen down when idle, saving heat &amp; battery wear. 0 = never sleep. A single tap wakes it instantly — no PIN, no lock.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisplayModal(false)}>Cancel</Button>
            <Button data-testid="display-settings-save" onClick={saveDisplay} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KioskPanel kioskId={panelKiosk} onClose={() => { setPanelKiosk(null); load(); }} />
      <LocationPanel locationId={panelLocation} onOpenKiosk={(id) => { setPanelLocation(null); setPanelKiosk(id); }} onClose={() => { setPanelLocation(null); load(); }} />
    </div>
  );
}
